import { useEffect, useRef, useCallback, useState } from "react";
import { useAppContext } from "@context/appContext";
import jsQR from "jsqr";
import ScanView from "@components/ScanView/ScanView";
import ResultView from "@components/ResultView/ResultView";
import { getScannedQRCodeStatus, StatusCode } from "@/ApiCalls";

interface PaymentModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}

const VALID_STATUSES: StatusCode[] = ["200", "404", "500"];
const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: "environment" },
  audio: false,
};

const PaymentModal = ({ showModal, setShowModal }: PaymentModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const hasScanned = useRef(false);

  const [cameraActive, setCameraActive] = useState(false);
  const [walletResponse, setWalletResponse] = useState<{
    status: StatusCode;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { totalPrice, clear } = useAppContext();

  const checkCameraPermission = async (): Promise<boolean> => {
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        if (result.state === "denied") {
          setError(
            "Camera toestemming geweigerd. Sta toegang toe via browserinstellingen."
          );
          return false;
        }
      }
    } catch (err) {
      console.warn("Permissions API not available or failed:", err);
    }
    return true;
  };

  const startCamera = async () => {
    setError(null);

    const hasPermission = await checkCameraPermission();
    if (!hasPermission) return;

    try {
      let stream = await navigator.mediaDevices.getUserMedia(
        CAMERA_CONSTRAINTS
      );

      if (!stream.getVideoTracks().length) {
        console.warn(
          "Geen camera gevonden met environment-facing mode. Valt terug op standaard."
        );
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current?.play();
            setCameraActive(true);
          } catch (err) {
            console.error("Kan video niet afspelen:", err);
            setError(
              "Camera kon niet gestart worden. Geef toestemming in je browserinstellingen."
            );
          }
        };
      }
    } catch (err: any) {
      handleCameraError(err);
    }
  };

  const handleCameraError = (err: any) => {
    console.error("Camera toegang mislukt:", err);
    const errorMessages: Record<string, string> = {
      NotAllowedError:
        "Camera toestemming geweigerd. Sta toegang toe via browserinstellingen.",
      NotReadableError:
        "Camera is in gebruik door een andere app. Sluit die apps en probeer opnieuw.",
      OverconstrainedError: "Gevraagde camera is niet beschikbaar.",
      NotFoundError: "Geen camera gevonden op dit apparaat.",
      default: "Camera toegang mislukt. Probeer opnieuw.",
    };

    setError(errorMessages[err.name] || errorMessages.default);
    setCameraActive(false);
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const scanQRCode = useCallback(() => {
    if (!cameraActive || !showModal || hasScanned.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (
      video &&
      canvas &&
      context &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    ) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

      if (qrCode?.data) {
        handleScannedData(qrCode.data);
        return;
      }
    }

    animationFrameRef.current = requestAnimationFrame(scanQRCode);
  }, [cameraActive, showModal]);

  const handleScannedData = async (data: string) => {
    hasScanned.current = true;
    stopCamera();

    let statusCode = extractStatusCode(data);

    if (!statusCode) {
      setError("Ongeldige QR-code. Geen bruikbare status gevonden.");
      setWalletResponse({ status: "404" });
      return;
    }

    try {
      const response = await getScannedQRCodeStatus(statusCode);
      setWalletResponse({ status: response.status || statusCode });
    } catch (err: any) {
      console.error("API fout:", err);
      setError(`API-fout: ${err.message || "onbekend"}`);
      setWalletResponse({ status: statusCode });
    }
  };

  const extractStatusCode = (data: string): StatusCode | null => {
    if (VALID_STATUSES.includes(data as StatusCode)) {
      return data as StatusCode;
    }

    try {
      const url = new URL(data);
      const dataParam = url.searchParams.get("data");
      if (dataParam && VALID_STATUSES.includes(dataParam as StatusCode)) {
        return dataParam as StatusCode;
      }
    } catch {
      // Niet geldig als URL
    }

    return null;
  };

  useEffect(() => {
    if (showModal) {
      hasScanned.current = false;
      setError(null);
      setWalletResponse(null);
      startCamera();
    } else {
      stopCamera();
    }

    return stopCamera;
  }, [showModal]);

  useEffect(() => {
    if (cameraActive) {
      animationFrameRef.current = requestAnimationFrame(scanQRCode);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [cameraActive, scanQRCode]);

  const tryAgain = () => {
    hasScanned.current = false;
    setError(null);
    setWalletResponse(null);
    startCamera();
  };

  const close = () => setShowModal(false);

  return walletResponse ? (
    <ResultView
      walletResponse={walletResponse}
      isVisible={showModal}
      error={error}
      onClear={() => {
        clear();
        setShowModal(false);
      }}
      onTryAgain={tryAgain}
    />
  ) : (
    <ScanView
      isVisible={showModal}
      cameraActive={cameraActive}
      totalPrice={totalPrice}
      videoRef={videoRef}
      canvasRef={canvasRef}
      onClose={close}
    />
  );
};

export default PaymentModal;
